import React, {useEffect, useMemo, useState} from "react";
import {Col, Form, Input, Row, Select, Switch, Upload, message} from "antd";
import {
    EyeOutlined,
    FileTextOutlined,
    PaperClipOutlined,
    PlusOutlined,
    ProfileOutlined,
    TagOutlined,
    UserOutlined
} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import {useStore} from "hooks/useStore";
import {
    TICKET_MAX_DESCRIPTION_LENGTH,
    TICKET_MAX_PHOTO_COUNT,
    TICKET_ONBOARDING_CATEGORY_TYPES,
    IS_TICKET_EXTERNAL_ACCESS_BLOCKED,
    TICKET_PHOTO_ACCEPT
} from "constants/ticket";
import {uploadTicketPhoto} from "./ticketPhotoUpload";
import {CreateModal, LabelText, OwnerNotice, VisibilityBox} from "./CreateTicket.styles";

// ownerId: talebin açıldığı kurye/şube
export const CreateTicketModal = ({
    visible,
    category,
    ownerId,
    ownerName,
    showOwnerVisibility = false,
    customerView = false,
    onCancel,
    onSuccess
}) => {
    const {t} = useTranslation(["tickets"]);
    const stores = useStore();
    const [form] = Form.useForm();
    const [topics, setTopics] = useState([]);
    const [selectedTopic, setSelectedTopic] = useState();
    const [selectedTitleId, setSelectedTitleId] = useState();
    const [fileList, setFileList] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!visible) {
            form.resetFields();
            setSelectedTopic(undefined);
            setSelectedTitleId(undefined);
            setFileList([]);
            return;
        }
        stores.ticketTypeStore.getTicketTypeOptions(category).then(response => {
            setTopics(response?.data?.topics || []);
        });
    }, [visible, category]);

    const titles = useMemo(() => topics.find(topic => topic.key === selectedTopic)?.titles || [], [
        topics,
        selectedTopic
    ]);
    const isPhotoRequired = !!titles.find(title => title.ticketTypeId === selectedTitleId)
        ?.isPhotoRequired;

    const handleSubmit = async () => {
        let values;
        try {
            values = await form.validateFields();
        } catch (error) {
            message.error(t("create.requiredFields", {ns: "tickets"}));
            return;
        }
        if (isPhotoRequired && fileList.length === 0) {
            message.error(t("create.photoRequired", {ns: "tickets"}));
            return;
        }

        setLoading(true);
        let photoUrls;
        try {
            photoUrls = await Promise.all(
                fileList.map(file => uploadTicketPhoto(file.originFileObj, stores.ticketStore))
            );
        } catch (error) {
            setLoading(false);
            message.error(t("create.uploadError", {ns: "tickets"}));
            return;
        }

        const result = await stores.ticketStore.createTicket({
            ticketTypeId: values.ticketTypeId,
            ...(ownerId ? {ownerId} : {}),
            description: values.description.trim(),
            photoUrls,
            ...(showOwnerVisibility ? {isShownToOwner: Boolean(values.isShownToOwner)} : {})
        });
        setLoading(false);
        if (result) {
            onSuccess();
        }
    };

    return (
        <CreateModal
            $customer={customerView}
            title={t("create.title", {ns: "tickets"})}
            visible={visible}
            onOk={handleSubmit}
            onCancel={onCancel}
            confirmLoading={loading}
            okText={t("create.submit", {ns: "tickets"})}
            cancelText={t("create.cancel", {ns: "tickets"})}
            width={600}
        >
            {ownerName && (
                <OwnerNotice>
                    <UserOutlined />
                    {t(
                        TICKET_ONBOARDING_CATEGORY_TYPES[category]
                            ? "create.createForApplication"
                            : "create.createFor",
                        {ns: "tickets", name: ownerName}
                    )}
                </OwnerNotice>
            )}
            <Form form={form} layout="vertical" initialValues={{isShownToOwner: false}}>
                <Row gutter={16}>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            label={
                                <LabelText>
                                    <TagOutlined />
                                    {t("create.topic", {ns: "tickets"})}
                                </LabelText>
                            }
                            name="topic"
                            rules={[
                                {
                                    required: true,
                                    message: t("create.topicRequired", {ns: "tickets"})
                                }
                            ]}
                            extra={
                                !topics.length ? t("create.noTopics", {ns: "tickets"}) : undefined
                            }
                        >
                            <Select
                                placeholder={t("create.topicPlaceholder", {ns: "tickets"})}
                                onChange={value => {
                                    setSelectedTopic(value);
                                    setSelectedTitleId(undefined);
                                    form.setFieldsValue({ticketTypeId: undefined});
                                }}
                            >
                                {topics.map(topic => (
                                    <Select.Option key={topic.key} value={topic.key}>
                                        {topic.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            label={
                                <LabelText>
                                    <ProfileOutlined />
                                    {t("create.ticketTitle", {ns: "tickets"})}
                                </LabelText>
                            }
                            name="ticketTypeId"
                            rules={[
                                {
                                    required: true,
                                    message: t("create.ticketTitleRequired", {ns: "tickets"})
                                }
                            ]}
                        >
                            <Select
                                disabled={!selectedTopic}
                                placeholder={
                                    selectedTopic
                                        ? t("create.ticketTitlePlaceholder", {ns: "tickets"})
                                        : t("create.ticketTitleDisabled", {ns: "tickets"})
                                }
                                onChange={setSelectedTitleId}
                            >
                                {titles.map(title => (
                                    <Select.Option
                                        key={title.ticketTypeId}
                                        value={title.ticketTypeId}
                                    >
                                        {title.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>
                <Form.Item
                    label={
                        <LabelText>
                            <FileTextOutlined />
                            {t("create.description", {ns: "tickets"})}
                        </LabelText>
                    }
                    name="description"
                    rules={[
                        {
                            required: true,
                            whitespace: true,
                            message: t("create.descriptionRequired", {ns: "tickets"})
                        }
                    ]}
                >
                    <Input.TextArea
                        rows={4}
                        maxLength={TICKET_MAX_DESCRIPTION_LENGTH}
                        placeholder={t("create.descriptionPlaceholder", {ns: "tickets"})}
                    />
                </Form.Item>
                <Form.Item
                    label={
                        <LabelText>
                            <PaperClipOutlined />
                            {`${t("create.photos", {ns: "tickets"})}${isPhotoRequired ? " *" : ""}`}
                        </LabelText>
                    }
                    extra={`${t("create.photoCount", {
                        ns: "tickets",
                        count: fileList.length,
                        max: TICKET_MAX_PHOTO_COUNT
                    })} · ${t("create.photoHint", {ns: "tickets", max: TICKET_MAX_PHOTO_COUNT})}`}
                >
                    <Upload
                        listType="picture-card"
                        multiple
                        accept={TICKET_PHOTO_ACCEPT}
                        fileList={fileList}
                        customRequest={({onSuccess}) => setTimeout(() => onSuccess("ok"), 0)}
                        beforeUpload={(file, newFiles) =>
                            fileList.length + newFiles.indexOf(file) < TICKET_MAX_PHOTO_COUNT
                                ? true
                                : Upload.LIST_IGNORE
                        }
                        onChange={({fileList: nextFileList}) =>
                            setFileList(nextFileList.slice(0, TICKET_MAX_PHOTO_COUNT))
                        }
                    >
                        {fileList.length < TICKET_MAX_PHOTO_COUNT && (
                            <div>
                                <PlusOutlined />
                                <div style={{marginTop: 8}}>
                                    {t("create.photoSelect", {ns: "tickets"})}
                                </div>
                            </div>
                        )}
                    </Upload>
                </Form.Item>
                {showOwnerVisibility && (
                    <VisibilityBox>
                        <LabelText style={{fontWeight: 600}}>
                            <EyeOutlined />
                            {t(`ownerVisibility.${category}`, {ns: "tickets"})}
                        </LabelText>
                        <Form.Item name="isShownToOwner" valuePropName="checked" noStyle>
                            <Switch disabled={IS_TICKET_EXTERNAL_ACCESS_BLOCKED} />
                        </Form.Item>
                    </VisibilityBox>
                )}
            </Form>
        </CreateModal>
    );
};
